import { useEffect, useRef } from 'react';
import { useStore } from 'core/store/store.mobx';
import useStyles from './use-scroll.styles';

// How long the highlight class stays on the element. Slightly longer than the 1.2s flash keyframe
// (use-scroll.styles.js) so the animation finishes before the class is removed.
const HIGHLIGHT_DURATION_MS = 1300;

// When a go-to-section target lives on a different tab, the tab is switched in the same handler
// that sets scrollTo, so the target element isn't in the DOM on the first effect run — it mounts a
// render or two later. Retry on animation frames until it appears (or we give up) instead of
// scrolling once and bailing. ~40 frames ≈ 650ms, plenty for a tab swap; bounded so a stale/unknown
// id can't poll forever.
const MAX_SCROLL_ATTEMPTS = 40;

export const useScroll = (scrollTo, afterScroll) => {
  const {
    offer: offerStore,
    account: {
      policies: { policy: policyStore }
    }
  } = useStore();
  const classes = useStyles();
  // Track the scrollTo value we've already handled so the effect is idempotent without a state
  // change that would retrigger it (and prematurely cancel its own in-flight timers).
  const handledRef = useRef(null);
  // The pending highlight-removal timer, kept in a ref so a fresh scroll (e.g. a quick repeat
  // navigation) can cancel the PREVIOUS flash's removal timer before starting its own. Without
  // this, the old timer could fire mid-second-flash and cut it short.
  const highlightTimerRef = useRef(null);
  useEffect(() => {
    if (!scrollTo || handledRef.current === scrollTo) {
      // Nothing to do. handledRef is re-armed when the flash finishes (see highlightTimerRef
      // below), not here — re-arming on scrollTo clearing (~1000ms) would land inside the flash
      // window and let a repeat navigation start a flash the prior timer then cuts short.
      return undefined;
    }

    let rafId;
    let clearTimer;
    let attempts = 0;

    const tryScroll = () => {
      const element = document.getElementById(scrollTo);
      if (!element) {
        attempts += 1;
        if (attempts < MAX_SCROLL_ATTEMPTS) {
          rafId = requestAnimationFrame(tryScroll);
        }
        return;
      }

      handledRef.current = scrollTo;
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });

      // Flash the section so the operator sees where they were sent (#30230 go-to-section).
      // Cancel any prior flash-removal timer first so it can't fire mid-flash and cut this one short.
      clearTimeout(highlightTimerRef.current);
      // Re-adding the class restarts the CSS animation even if the element was just highlighted.
      element.classList.remove(classes.highlighted);
      // Force reflow so removing + re-adding the class re-triggers the animation.
      void element.offsetWidth;
      element.classList.add(classes.highlighted);
      highlightTimerRef.current = setTimeout(() => {
        element.classList.remove(classes.highlighted);
        // Re-arm only after the flash finishes — not at clearTimer (1000ms) — so a repeat navigation
        // in the 1000–1300ms window can't start a new flash that this timer would then cut short.
        handledRef.current = null;
      }, HIGHLIGHT_DURATION_MS);

      if (afterScroll) {
        afterScroll();
      }
      clearTimer = setTimeout(() => {
        if (offerStore) {
          offerStore.setScrollTo(null);
        } else if (policyStore) {
          policyStore.setScrollTo(null);
        }
      }, 1000);
    };

    tryScroll();

    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      clearTimeout(clearTimer);
    };
  }, [scrollTo, afterScroll, offerStore, policyStore, classes.highlighted]);
};
